import React from 'react';
import m from 'moment';
import { object, func, arrayOf, bool } from 'prop-types';
import { find, isEqual } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import AddIcon from '@material-ui/icons/Add';
import LinearProgress from '@material-ui/core/LinearProgress';
import Grid from '@material-ui/core/Grid';
import { Typography } from '@material-ui/core';
import { Translation } from 'react-i18next';
import ErrorMsg from '../components/ErrorMsg';
import ApplicationsFilter from './ApplicationsFilter';
import ApplicationsPagination from './ApplicationsPagination';

const styles = theme => ({
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#357a38',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  table: {
    minWidth: 700,
  },
  tableContainer: {
    margin: theme.spacing(4),
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
  },
  icon: {
    margin: theme.spacing(0.5),
  },
  progress: {
    flexGrow: 1,
  },
  linkButton: {
    margin: theme.spacing(1),
  },
  docsContainer: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E1E1E1',
  },
  titleContainer: {
    marginTop: theme.spacing(1) - 30,
    display: 'flex',
    justifyContent: 'center',
    width: 200,
    backgroundColor: '#ffffff',
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    color: '#B3B3B3',
    fontSize: 25,
  },
  root: {},
  headerContainer: {},
  titleStyle: {},
  subTitle: {},
  itemMidContainer: {},
  footerContainer: {},
  footerBar: {},
  textFooterTerms: {},
  textFooter: {},
  trademarkText: {},
});

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class PaperSheet extends React.Component {
  componentDidMount() {
    const {
      isLAO,
      currentUserAssignmentId,
      getApplications,
      setFilters,
    } = this.props;
    if (isLAO) {
      const from = m()
        .subtract(30, 'days')
        .format('YYYY-MM-DD');
      const to = m().format('YYYY-MM-DD');
      setFilters({
        status: 'SUBMITTED',
        submittedAt: `${from} ${to}`,
        createdByAssignmentId: currentUserAssignmentId,
      });
    }
    getApplications();
  }

  componentDidUpdate(prevProps) {
    const {
      applications,
      getApplications,
      filters,
      pagination,
      history,
      isFetching,
      isLAO,
    } = this.props;
    if (
      !isFetching &&
      prevProps.isFetching &&
      applications.length === 0 &&
      !isLAO
    ) {
      history.push('/application');
    }
    if (
      !isEqual(prevProps.filters, filters) ||
      !isEqual(prevProps.pagination, pagination)
    ) {
      getApplications();
    }
  }

  setAndRouteTo = (uuid, route) => {
    const application = find(this.props.applications, { uuid });
    this.props.clearCurrentApplication();
    this.props.setApplication(application);
    this.props.history.push(route);
  };

  viewApplication = uuid => this.setAndRouteTo(uuid, '/view');

  resumeApplication = uuid => this.setAndRouteTo(uuid, '/application');

  resumeDeclaration = uuid => this.setAndRouteTo(uuid, '/declaration');

  payForApplication = uuid => this.setAndRouteTo(uuid, `/payment/${uuid}`);

  respondToOfficer = uuid => this.setAndRouteTo(uuid, '/message/officer');

  messageToApplicant = uuid => this.setAndRouteTo(uuid, '/message/applicant');

  onClickNew = () => {
    this.props.clearCurrentApplication();
    this.props.history.push('/application');
  };

  render() {
    const {
      classes,
      applications,
      permissions,
      isFetching,
      error,
      isLAO,
    } = this.props;

    return (
      <Translation>
        {t => {
          return (
            <div>
              {!!error && <ErrorMsg error={error} />}
              <div align="right">
                <Button
                  variant="contained"
                  type="submit"
                  className={classes.button}
                  onClick={this.onClickNew}
                >
                  <AddIcon className={classes.icon} fontSize="small" />
                  {t('newApplication')}
                </Button>
              </div>
              <Grid className={classes.docsContainer}>
                <Typography
                  className={classes.titleContainer}
                  variant="h6"
                  align="left"
                  gutterBottom
                >
                  {t('myApplications')}
                </Typography>
                {isLAO && <ApplicationsFilter />}
                <Grid className={classes.tableContainer}>
                  {isFetching && (
                    <LinearProgress className={classes.progress} />
                  )}
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <CustomTableCell>{t('applicationId')}</CustomTableCell>
                        <CustomTableCell>{t('shortCode')}</CustomTableCell>
                        <CustomTableCell>
                          {t('applicationName')}
                        </CustomTableCell>
                        <CustomTableCell>{t('status')}</CustomTableCell>
                        <CustomTableCell>{t('submissionDate')}</CustomTableCell>
                        <CustomTableCell>{t('actions')}</CustomTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {applications.map(row => {
                        const rowStatus = row.status;
                        return (
                          <TableRow className={classes.row} key={row.ian || ''}>
                            <CustomTableCell component="th" scope="row">
                              {row.ian}
                            </CustomTableCell>
                            <CustomTableCell>{row.shortCode}</CustomTableCell>
                            <CustomTableCell>{`${row.nameFirst ||
                              t('unspecified')} ${row.nameLast ||
                              ''}`}</CustomTableCell>
                            <CustomTableCell>
                              {row.statusDisplay}
                            </CustomTableCell>
                            <CustomTableCell>{row.submittedAt}</CustomTableCell>
                            <CustomTableCell>
                              {rowStatus !== 'IN_CREATION' && (
                                <Button
                                  color="primary"
                                  className={classes.linkButton}
                                  onClick={() => this.viewApplication(row.uuid)}
                                >
                                  {t('view')}
                                </Button>
                              )}

                              {(rowStatus === 'IN_CREATION' ||
                                rowStatus === 'COMPLETED' ||
                                rowStatus === 'CANCELED') && (
                                <Button
                                  color="primary"
                                  className={classes.linkButton}
                                  onClick={() =>
                                    this.resumeApplication(row.uuid)
                                  }
                                >
                                  {t('resume')}
                                </Button>
                              )}
                              {permissions.includes(
                                'waf_visaapp_email_applicant'
                              ) && (
                                <Button
                                  color="primary"
                                  className={classes.linkButton}
                                  onClick={() =>
                                    this.messageToApplicant(row.uuid)
                                  }
                                >
                                  {t('messageToApplicant')}
                                </Button>
                              )}
                              {rowStatus === 'AWAITING_APPLICANT_FEEDBACK' && (
                                <Button
                                  color="primary"
                                  className={classes.linkButton}
                                  onClick={() =>
                                    this.respondToOfficer(row.uuid)
                                  }
                                >
                                  {t('respondToVisaOfficer')}
                                </Button>
                              )}
                            </CustomTableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <ApplicationsPagination />
                </Grid>
              </Grid>
            </div>
          );
        }}
      </Translation>
    );
  }
}

PaperSheet.propTypes = {
  classes: object.isRequired,
  getApplications: func.isRequired,
  applications: arrayOf(object).isRequired,
  isFetching: bool.isRequired,
  setApplication: func.isRequired,
  clearCurrentApplication: func.isRequired,
};

export default withStyles(styles)(PaperSheet);



// WEBPACK FOOTER //
// ./src/view/scenes/Applications/Applications.js